import { useContext, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import utility from "src/utils/utility"
import AppInfoContext from "src/provider/state-manager/appInfoProvider"
import VisibilityContext from "src/provider/state-manager/visibilityProvider"
import ApiContext from "src/provider/API/call-service"
import { AppSpan, AppText, Button, Circle, CustomContainer, FlexRow, FormGroupWithIcon, GridContainer, PageTitle, PageWrapper } from "src/style"
import { HiOutlineDotsHorizontal } from "react-icons/hi"
import { IoMdArrowBack, IoMdArrowForward } from "react-icons/io"
import { FiChevronDown } from "react-icons/fi"
import { products } from "src/utils/mock-data"
import { Dropdown, ProductView } from "src/component"
import { MdOutlineKeyboardArrowDown } from "react-icons/md";
import { Product, ServiceCategory } from "src/model"
import {ReactComponent as InProgressIcon} from 'src/assets/img/in-progress-icon.svg'

const Store: React.FC = () => {
    const navigate = useNavigate()
    const {API} = useContext(ApiContext)
    const {info: {selectedServiceCategory}, setInfoProperty } = useContext(AppInfoContext)
    const {decisionBox} = useContext(VisibilityContext)
    const [selectedService, setSelectedService] = useState<ServiceCategory|null>(null)
    const [pageNumber, setPageNumber] = useState(1)
    const [serviceCategories, setServiceCategories] = useState<Array<any>>([])
    const [productInfo, setProductInfo] = useState<any>(null)
    const [products, setProducts] = useState<Array<any>>([])
    const [sortBy, setSortBy] = useState('Installation')

    useEffect(() => {
        handleFetchServiceCategories()
    }, [selectedServiceCategory])

    async function handleFetchServiceCategories () {
        const response = await API.getPublicServiceCategories({
            pageNumber: 1, pageSize: 100, 
            sortOrder: 'ASC', sortColumn: 'name'
        })
        
        if (response) {
            const responseData = response.data
            setServiceCategories(responseData)
            setSelectedService(selectedServiceCategory || responseData[0])
            handleFetchProducts(selectedServiceCategory?.id || responseData[0]?.id)
        }
    }

    async function handleFetchProducts (serviceCategoryId = selectedService!?.id, pageNumber = 1, sortColumn = sortBy) {
        const response = await API.getPublicProducts({
            pageNumber, 
            pageSize: 10, 
            sortColumn, 
            sortOrder: 'DESC',
            filter: { serviceCategoryId }
        })

        if (response) {
            setProductInfo(response)
            setProducts(response.data)
        }
    }

    function handleSelectedService (data: any) {
        data = JSON.parse(data)
        setSelectedService(data)
        handleFetchProducts(data.id)
    }

    function handleSortBy (sortBy: string) {
        setSortBy(sortBy)
        handleFetchProducts(selectedService!?.id, 1, sortBy)
    }

    function handleProductPagination (pageNumber: number) {
        setPageNumber(pageNumber)
        handleFetchProducts(selectedService!?.id, pageNumber)
    }

    async function handleProduct (product: Product) {
        await setInfoProperty('selectedProduct', product)
        navigate(`/public/client/discover/product/${product.id}`)
    }

    return (
        <>
            <PageWrapper>
                <FlexRow justifyContent="space-between" gap="2" wrap="wrap">
                    <PageTitle 
                        color='#101828' textSize='3' fontWeight="500"
                    >
                        Store
                    </PageTitle>
                    <Dropdown 
                        trigger={
                            <FlexRow 
                                justifyContent="space-between" 
                                width="23.3" height="4" gap="2"
                                radius="0.8" borderColor="#D0D5DD"
                                padding="1.5"
                            >
                                <AppSpan cursor="pointer">{sortBy || 'Sort by'}</AppSpan>
                                <MdOutlineKeyboardArrowDown fill='#ADB5BD' fontSize='1.7rem' />
                            </FlexRow>
                        }
                        options={[
                            {value: 'Name', label: 'Name'},
                            {value: 'Rating', label: 'Rating'},
                            {value: 'Installation', label: 'Installation'}
                        ]}
                        onSelect={(value: string) => handleSortBy(value)}
                        width={18}
                    />
                </FlexRow>
                <CustomContainer topMargin="2">
                    <CustomContainer
                        className="services-dropdown-container"
                        bottomMargin="2"
                    >
                        <FormGroupWithIcon>
                            <label>Service</label>
                            <div className="input-with-icon" style={{height: '4rem'}}>
                                <select 
                                    name='role'
                                    value={JSON.stringify(selectedService)}
                                    style={{color: selectedService ? '#000000' : '#667085'}}
                                    onChange={(e) => handleSelectedService(e.target.value)}
                                    autoComplete="off"
                                >
                                    {
                                        serviceCategories?.map((service, index) => {
                                            const isAvailable = service.availabilityStatus === 'Available'

                                            return (
                                                <option value={JSON.stringify(service)} key={index}>{`${service.name}${!isAvailable && ' (Coming Soon)'}`}</option>
                                            )
                                        })
                                    }
                                </select>
                                <FiChevronDown className="input-with-icon-tag" />
                            </div>
                        </FormGroupWithIcon>
                    </CustomContainer>
                    <FlexRow 
                        justifyContent="space-between" 
                        alignItems="flex-start" 
                        gap="8" topMargin="3"
                    >
                        <CustomContainer
                            width="32"
                            className="service-types-container"
                        >
                            {
                                serviceCategories.map((categories, index) => {
                                    const isAvailable = categories.availabilityStatus === 'Available'
                                    const isSelected = selectedService?.name === categories.name

                                    return (
                                        <CustomContainer
                                            padding="1" radius="0.6"
                                            topPadding="0.8" bottomPadding="0.8"
                                            bgColor={selectedService?.name === categories.name ? '#E7F6F5' : 'transparent'}
                                            key={index}
                                            onClick={() => handleSelectedService(JSON.stringify(categories))}
                                        >
                                            <FlexRow gap="1">
                                                <AppSpan
                                                    color={isSelected ? '#0A756F' : '#667085'}
                                                    fontWeight="500"
                                                    cursor="pointer"
                                                >
                                                    {categories.name}
                                                </AppSpan>
                                                { !isAvailable &&
                                                    <CustomContainer
                                                        bgColor={isSelected ? '#E7F6F5' : '#F2F4F7'}
                                                        width='6.5' radius='1.6'
                                                        padding='0.2' topMargin='0.5'
                                                        leftPadding='0.5' rightPadding='0.5'
                                                        className="fixed-flex-shape"
                                                    >
                                                        <AppText
                                                            textSize='0.8' fontWeight='500'
                                                            color={isSelected ? '#0A756F' : '#344054'}
                                                        >
                                                            Coming soon
                                                        </AppText>
                                                    </CustomContainer>
                                                }
                                            </FlexRow>
                                        </CustomContainer>
                                    )
                                })
                            }
                        </CustomContainer>
                        
                        { selectedService?.availabilityStatus === 'Available' ?
                            <CustomContainer>
                                <FlexRow 
                                    gap="2" wrap="wrap"
                                    alignItems='stretch'
                                >
                                    {
                                        products.map((product, index) => {
                                            return (
                                                <ProductView
                                                    key={index}
                                                    product={product}
                                                    isComingSoon={product?.integrationStatus === 'ComingSoon'}
                                                    onClick={() => handleProduct(product)} 
                                                />
                                            )
                                        })
                                    }
                                </FlexRow>

                                { !!products?.length &&
                                    <FlexRow 
                                        justifyContent="space-between"
                                        gap="2" topMargin="3" topPadding="2"
                                        style={{borderTop: '1px solid #EAECF0'}}
                                    >
                                        <Button
                                            width='12'
                                            titleSize='1.4' fontWeight="500"
                                            height='4' radius='0.8' color="#667085"
                                            bgColor="transparent"
                                            hoverBgColor='#fff' hoverBorderColor='#0D968F'
                                            className="fixed-flex-shape"
                                            style={{visibility: (pageNumber - 1) > 0 ? "visible" : "hidden"}}
                                            onClick={() => handleProductPagination(pageNumber - 1)}
                                        >
                                            <AppSpan rightMargin="1" textSize="1.6"><IoMdArrowBack /></AppSpan>
                                            Previous
                                        </Button>
                                        <FlexRow 
                                            width="auto" sizeUnit=""
                                            gap="0.2" wrap="wrap"
                                            className="pagination-numbers-container"
                                        >
                                            {
                                                [1, 2, 3].map((item, index) => {
                                                    return (
                                                        (item <= productInfo?.totalPages && productInfo?.totalPages != 1) &&
                                                            <Circle
                                                                size="4" key={index}
                                                                bgColor={pageNumber === item ? '#0D968F' : 'transparent'}
                                                                onClick={() => handleProductPagination(item)}
                                                            >
                                                                <AppSpan
                                                                    fontWeight="500" cursor="pointer"
                                                                    color={pageNumber === item ? '#ffffff' : '#667085'}
                                                                >
                                                                    {item}
                                                                </AppSpan>
                                                            </Circle>
                                                    )
                                                })
                                            }
                                            <Circle size="4">
                                                <HiOutlineDotsHorizontal fill="#667085" />
                                            </Circle>
                                            {
                                                [8, 9, 10].map((item, index) => {
                                                    return (
                                                        item <= productInfo?.totalPages &&
                                                            <Circle
                                                                size="4" key={index}
                                                                bgColor={pageNumber === item ? '#0D968F' : 'transparent'}
                                                                onClick={() => handleProductPagination(item)}
                                                            >
                                                                <AppSpan
                                                                    fontWeight="500" cursor="pointer"
                                                                    color={pageNumber === item ? '#ffffff' : '#667085'}
                                                                >
                                                                    {item}
                                                                </AppSpan>
                                                            </Circle>
                                                    )
                                                })
                                            }
                                        </FlexRow>
                                        <Button
                                            width='12'
                                            titleSize='1.4' fontWeight="500"
                                            height='4' radius='0.8' color="#667085"
                                            bgColor="transparent"
                                            hoverBgColor='#fff' hoverBorderColor='#0D968F'
                                            className="fixed-flex-shape"
                                            style={{visibility: (pageNumber + 1 <= productInfo?.totalPages) ? "visible" : "hidden"}}
                                            onClick={() => handleProductPagination(pageNumber + 1)}
                                        >
                                            Next
                                            <AppSpan leftMargin="1" textSize="1.6"><IoMdArrowForward /></AppSpan>
                                        </Button>
                                    </FlexRow>
                                }
                            </CustomContainer>
                            :
                            <GridContainer
                                style={{height: 'calc(100vh - 30rem)'}}
                            >
                                <GridContainer width="auto" sizeUnit="">
                                    <InProgressIcon className="icon-component" />
                                    <AppText textSize="1.6" topMargin="2">
                                        This category is coming soon
                                    </AppText>
                                </GridContainer>
                            </GridContainer>
                        }
                    </FlexRow>
                </CustomContainer>
            </PageWrapper>
        </>
    )
}

export default utility.routeData('store', 'Store', <Store/>)
